import { useState, useEffect } from "react";
import { Container, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ShootingStar, Tool } from "@mynaui/icons-react";
import QuickActionCard from "../components/QuickActionCard";
import SectionHeader from "../components/SectionHeader";
import { quickActions } from "../data/dashboardMockData";
import AssignedAssets from "../components/AssignedAsset";
import { useZone } from "../../../shared/context/ZoneContext";
import dashboardService from "../../../services/dashboardService";

const TechnicianDashboard = () => {
  const navigate = useNavigate();
  const { selectedZone } = useZone();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedZone) return;
    setLoading(true);
    setAssets([]);
    dashboardService
      .getAssignedAssets(selectedZone)
      .then((res) => {
        const mapped = (res.data?.data ?? []).map(mapToAssetShape);
        setAssets(mapped);
      })
      .catch((err) =>
        console.error("Failed to fetch assigned assets:", err.message),
      )
      .finally(() => setLoading(false));
  }, [selectedZone]);

  const handleQuickAction = (actionId) =>
    console.log("Quick action clicked:", actionId);
  const handleAssetClick = (assetId) => navigate(`/asset/${assetId}`);

  return (
    <Box
      sx={{
        backgroundColor: "#F5F7FA",
        minHeight: "100vh",
        py: { xs: 2, sm: 4 },
      }}
    >
      <Container maxWidth="xl">
        {/* Quick Actions */}
        <Box sx={{ mb: 5 }}>
          <SectionHeader icon={ShootingStar} title="Quick Actions" />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 3,
            }}
          >
            {quickActions.map((action) => (
              <QuickActionCard
                key={action.id}
                action={action}
                onClick={() => handleQuickAction(action.id)}
              />
            ))}
          </Box>
        </Box>

        {/* Assigned Assets */}
        <Box>
          <SectionHeader icon={Tool} title="Assigned Assets" />
          <AssignedAssets
            assets={assets}
            loading={loading}
            onAssetClick={handleAssetClick}
          />
        </Box>
      </Container>
    </Box>
  );
};

// Map API response to card shape
const mapToAssetShape = (a) => ({
  id: a.id,
  name: a.name,
  tag: a.tag,
  priority: a.riskLevel ?? "Unknown",
  status: a.status ?? "Unknown",
  healthScore: a.healthScore ?? 0,
  location: {
    workspace: a.plantName ?? "N/A",
    zone: a.areaName ?? "N/A",
    area: a.tag ?? "N/A",
  },
  alert: {
    type: (a.failureProbability ?? 0) >= 0.7 ? "error" : "warning",
    message: `${((a.failureProbability ?? 0) * 100).toFixed(2)}% failure probability`,
    confidence: `${a.confidence ?? 0}% confidence`,
  },
  rul: {
    label: "RUL",
    value: a.rulDays ? `${a.rulDays} days` : "N/A",
  },
  workOrders: {
    label: "Work Orders",
    value: a.workOrderCount ?? 0,
  },
  nextService: {
    label: "Next Service",
    value: a.nextServiceDate
      ? new Date(a.nextServiceDate).toLocaleDateString("en-US", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "N/A",
  },
  sensors: {
    airTemperature: {
      label: "Air Temperature",
      value: a.airTemperatureC != null ? `${a.airTemperatureC} °C` : "N/A",
      normal: "0-40",
    },
    processTemperature: {
      label: "Process Temperature",
      value: a.processTemperatureC != null ? `${a.processTemperatureC} °C` : "N/A",
      normal: "0-80",
    },
    rotationalSpeed: {
      label: "Rotational Speed",
      value: (a.roationalSpeed ?? a.rotationalSpeed) != null ? `${a.roationalSpeed ?? a.rotationalSpeed} RPM` : "N/A",
      normal: "600-3000",
    },
    torque: {
      label: "Torque",
      value: a.torque != null ? `${a.torque} N·m` : "N/A",
      normal: "0-75",
    },
    toolWear: {
      label: "Tool Wear",
      value: a.toolWear != null ? `${a.toolWear} min` : "N/A",
      normal: "0-150",
    },
  },
});

export default TechnicianDashboard;
